import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../api";
import { Async } from "../components/Async";
import { day, money, month } from "../format";
import type { Heatmap, HistoryPoint, OriginMonthStats } from "../types";

// Reihenfolge bleibt stabil, damit ein Abflughafen nicht zwischen zwei
// Ladevorgaengen die Farbe wechselt.
const SERIES_COLOURS = ["#1d4ed8", "#0f7b41", "#a4322b", "#7c3aed", "#b45309", "#0e7490"];

export function History() {
  const history = useQuery({ queryKey: ["history"], queryFn: () => api.history(90) });
  const heatmap = useQuery({ queryKey: ["heatmap"], queryFn: () => api.heatmap(30) });
  const stats = useQuery({ queryKey: ["stats"], queryFn: () => api.stats(90) });

  return (
    <>
      <h1>Historie</h1>
      <p className="lede">
        Wie sich die Preise bewegen und in welchen Zeiträumen es günstig ist. Die Zahlen sind
        Entscheidungshilfe für die Alarmschwelle – abgeleitet wird sie daraus nicht.
      </p>

      <h2>Günstigster Preis je Tag</h2>
      <Async query={history} empty="Noch keine Beobachtungen.">
        {(points) => <PriceChart points={points} />}
      </Async>

      <h2>Preis-Heatmap nach Abflugtag</h2>
      <Async query={heatmap}>{(data) => <HeatmapTable heatmap={data} />}</Async>

      <h2>Verteilung je Abflughafen und Reisemonat</h2>
      <Async query={stats} empty="Noch keine Beobachtungen im Rückblickfenster.">
        {(rows) => <StatsTable rows={rows} />}
      </Async>
    </>
  );
}

function PriceChart({ points }: { points: HistoryPoint[] }) {
  const origins = [...new Set(points.map((point) => point.origin))].sort();
  const byDate = new Map<string, Record<string, number | string>>();
  for (const point of points) {
    const row = byDate.get(point.observed_on) ?? { observed_on: point.observed_on };
    row[point.origin] = point.min_price_cents / 100;
    byDate.set(point.observed_on, row);
  }
  const data = [...byDate.values()].sort((a, b) =>
    String(a.observed_on).localeCompare(String(b.observed_on)),
  );

  // Ein einzelner Tag ergibt eine Linie ohne Verlauf. Eine fast leere
  // Flaeche sieht aus wie ein Fehler, ist aber nur der Anfang der
  // Datensammlung -- das gehoert dazugesagt.
  if (data.length < 2) {
    return (
      <div className="notice">
        Bisher nur ein Beobachtungstag. Ein Verlauf entsteht erst, wenn der Worker mehrmals
        gelaufen ist – Preishistorie lässt sich nicht nachholen, sie entsteht mit der Zeit.
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="observed_on"
            tickFormatter={(value: string) => day(value)}
            stroke="var(--muted)"
            fontSize={12}
          />
          <YAxis
            stroke="var(--muted)"
            fontSize={12}
            width={64}
            tickFormatter={(value: number) => `${value} €`}
          />
          <Tooltip
            formatter={(value: number) => `${value} €`}
            labelFormatter={(value: string) => day(value)}
            contentStyle={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              color: "var(--text)",
            }}
          />
          {origins.map((origin, index) => (
            <Line
              key={origin}
              type="monotone"
              dataKey={origin}
              name={origin}
              stroke={SERIES_COLOURS[index % SERIES_COLOURS.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <p className="muted">
        Eine Linie je Abflughafen: {origins.join(", ") || "keine Daten"}
      </p>
    </div>
  );
}

function HeatmapTable({ heatmap }: { heatmap: Heatmap }) {
  if (heatmap.cells.length === 0) {
    return <div className="notice">Noch keine Beobachtungen im Rückblickfenster.</div>;
  }

  const dates = [...new Set(heatmap.cells.map((cell) => cell.depart_date))].sort();
  const origins = [...new Set(heatmap.cells.map((cell) => cell.origin))].sort();
  const byKey = new Map(heatmap.cells.map((cell) => [`${cell.origin}|${cell.depart_date}`, cell]));

  const prices = heatmap.cells.map((cell) => cell.min_price_cents);
  const lowest = Math.min(...prices);
  const highest = Math.max(...prices);

  /** Grün für günstig, rot für teuer, relativ zur beobachteten Spanne. */
  const shade = (cents: number): string => {
    if (highest === lowest) return "transparent";
    const position = (cents - lowest) / (highest - lowest);
    const hue = 140 - position * 140;
    return `hsl(${hue} 55% 50% / 0.22)`;
  };

  return (
    <div className="scroll">
      <table className="heat">
        <thead>
          <tr>
            <th>Abflug</th>
            {origins.map((origin) => (
              <th key={origin} className="num">
                {origin}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dates.map((date) => (
            <tr key={date}>
              <td>{day(date)}</td>
              {origins.map((origin) => {
                const cell = byKey.get(`${origin}|${date}`);
                if (!cell) {
                  return (
                    <td key={origin} className="empty">
                      –
                    </td>
                  );
                }
                return (
                  <td
                    key={origin}
                    className="cell"
                    style={{ background: shade(cell.min_price_cents) }}
                    title={`${cell.observations} Beobachtung(en)`}
                  >
                    {money(cell.min_price_cents)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">
        Günstigster bekannter Preis je Abflughafen und Abflugtag, Rückblick 30 Tage. Leere Zellen
        sind Tage, die das Abtastraster nicht getroffen hat.
      </p>
    </div>
  );
}

function StatsTable({ rows }: { rows: OriginMonthStats[] }) {
  return (
    <div className="scroll">
      <table>
        <thead>
          <tr>
            <th>Abflughafen</th>
            <th>Reisemonat</th>
            <th className="num">Beobachtungen</th>
            <th className="num">Minimum</th>
            <th className="num">P10</th>
            <th className="num">Median</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.origin}-${row.month}`}>
              <td>{row.origin}</td>
              <td>{month(row.month)}</td>
              <td className="num">{row.observations}</td>
              <td className="num">{money(row.min_cents)}</td>
              <td className="num">{money(row.p10_cents)}</td>
              <td className="num">{money(row.median_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
